
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Ticket, Plus, Trash2 } from 'lucide-react';

interface Coupon {
  id: string;
  code: string;
  discount_type: string;
  discount_value: number;
  description: string;
  usage_limit: number;
  per_user_limit: number;
  usage_count: number;
  expires_at: string;
  is_active: boolean;
  created_at: string;
  applicable_plans: string[];
}

const AdminCouponManagement = () => {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 0,
    description: '',
    usage_limit: 100,
    per_user_limit: 1,
    expires_at: '',
    applicable_plans: ['monthly', 'yearly'],
  });
  const queryClient = useQueryClient();

  const { data: coupons, isLoading } = useQuery({
    queryKey: ['admin-coupons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Coupon[];
    },
  });

  const createCoupon = useMutation({
    mutationFn: async (couponData: typeof newCoupon) => {
      console.log('Creating coupon with data:', couponData);
      
      const { data, error } = await supabase
        .from('coupons')
        .insert([{
          code: couponData.code,
          discount_type: couponData.discount_type,
          discount_value: couponData.discount_value,
          description: couponData.description,
          usage_limit: couponData.usage_limit,
          per_user_limit: couponData.per_user_limit,
          expires_at: couponData.expires_at ? new Date(couponData.expires_at).toISOString() : null,
          applicable_plans: couponData.applicable_plans,
          is_active: true,
          usage_count: 0
        }])
        .select()
        .single();
      
      if (error) {
        console.error('Coupon creation error:', error);
        throw error;
      }
      
      console.log('Coupon created successfully:', data);
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Coupon created successfully."
      });
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
      setIsCreateDialogOpen(false);
      setNewCoupon({
        code: '',
        discount_type: 'percentage',
        discount_value: 0,
        description: '',
        usage_limit: 100,
        per_user_limit: 1,
        expires_at: '',
        applicable_plans: ['monthly', 'yearly'],
      });
    },
    onError: (error) => {
      console.error('Error creating coupon:', error);
      toast({
        title: "Error",
        description: "Failed to create coupon.",
        variant: "destructive"
      });
    },
  });

  const toggleCouponStatus = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('coupons')
        .update({ is_active })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Coupon status updated."
      });
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
    },
    onError: (error) => {
      console.error('Error updating coupon:', error);
      toast({
        title: "Error",
        description: "Failed to update coupon.",
        variant: "destructive"
      });
    },
  });

  const deleteCoupon = useMutation({
    mutationFn: async (couponId: string) => {
      const { error } = await supabase
        .from('coupons')
        .delete()
        .eq('id', couponId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Coupon deleted successfully."
      });
      queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
    },
    onError: (error) => {
      console.error('Error deleting coupon:', error);
      toast({
        title: "Error",
        description: "Failed to delete coupon.",
        variant: "destructive"
      });
    },
  });

  const handleCreateCoupon = () => {
    if (!newCoupon.code || !newCoupon.description || newCoupon.discount_value <= 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (newCoupon.applicable_plans.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one applicable plan.",
        variant: "destructive"
      });
      return;
    }

    createCoupon.mutate(newCoupon);
  };

  const handlePlanChange = (plan: string, checked: boolean) => {
    setNewCoupon(prev => ({
      ...prev,
      applicable_plans: checked
        ? [...prev.applicable_plans, plan]
        : prev.applicable_plans.filter(p => p !== plan)
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDiscount = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `$${value}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Coupon Management
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Coupon
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Coupon</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="code">Coupon Code</Label>
                  <Input
                    id="code"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SAVE20"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={newCoupon.description}
                    onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                    placeholder="Brief description of the coupon"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="discount_type">Type</Label>
                    <Select value={newCoupon.discount_type} onValueChange={(value) => setNewCoupon({ ...newCoupon, discount_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="discount_value">Value</Label>
                    <Input
                      id="discount_value"
                      type="number"
                      value={newCoupon.discount_value}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: Number(e.target.value) })}
                      placeholder={newCoupon.discount_type === 'percentage' ? '10' : '5'}
                    />
                  </div>
                </div>
                <div>
                  <Label>Applicable Plans</Label>
                  <div className="flex flex-col space-y-2 mt-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="monthly"
                        checked={newCoupon.applicable_plans.includes('monthly')}
                        onCheckedChange={(checked) => handlePlanChange('monthly', checked as boolean)}
                      />
                      <Label htmlFor="monthly">Monthly Plan</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="yearly"
                        checked={newCoupon.applicable_plans.includes('yearly')}
                        onCheckedChange={(checked) => handlePlanChange('yearly', checked as boolean)}
                      />
                      <Label htmlFor="yearly">Yearly Plan</Label>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="usage_limit">Usage Limit</Label>
                    <Input
                      id="usage_limit"
                      type="number"
                      value={newCoupon.usage_limit}
                      onChange={(e) => setNewCoupon({ ...newCoupon, usage_limit: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="per_user_limit">Per User Limit</Label>
                    <Input
                      id="per_user_limit"
                      type="number"
                      value={newCoupon.per_user_limit}
                      onChange={(e) => setNewCoupon({ ...newCoupon, per_user_limit: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="expires_at">Expiry Date (Optional)</Label>
                  <Input
                    id="expires_at"
                    type="date"
                    value={newCoupon.expires_at}
                    onChange={(e) => setNewCoupon({ ...newCoupon, expires_at: e.target.value })}
                  />
                </div>
                <Button 
                  onClick={handleCreateCoupon} 
                  disabled={createCoupon.isPending}
                  className="w-full"
                >
                  {createCoupon.isPending ? "Creating..." : "Create Coupon"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Applicable Plans</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons?.map((coupon) => (
                <TableRow key={coupon.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{coupon.code}</div>
                      <div className="text-sm text-gray-500">{coupon.description}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {formatDiscount(coupon.discount_type, coupon.discount_value)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {coupon.applicable_plans?.map((plan) => (
                        <Badge key={plan} variant="outline" className="text-xs">
                          {plan.charAt(0).toUpperCase() + plan.slice(1)}
                        </Badge>
                      )) || <span className="text-sm text-gray-500">All Plans</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {coupon.usage_count || 0} / {coupon.usage_limit || '∞'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {coupon.expires_at ? (
                      <span className={`text-sm ${new Date(coupon.expires_at) < new Date() ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatDate(coupon.expires_at)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">No expiry</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={coupon.is_active ? 'default' : 'secondary'}>
                      {coupon.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleCouponStatus.mutate({ 
                          id: coupon.id, 
                          is_active: !coupon.is_active 
                        })}
                        disabled={toggleCouponStatus.isPending}
                      >
                        {coupon.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete the coupon "{coupon.code}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteCoupon.mutate(coupon.id)}
                              disabled={deleteCoupon.isPending}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {deleteCoupon.isPending ? "Deleting..." : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCouponManagement;
